import ReactMarkdown, { type Components } from 'react-markdown'
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize'
import remarkGfm from 'remark-gfm'

// Banner messages are admin-authored markdown, but we still sanitize defensively
// (defense-in-depth; XSS is a known class in this codebase). Policy:
//   - No raw HTML (react-markdown does not render it unless rehype-raw is added;
//     we never add rehype-raw).
//   - No images at all (banners are text; blocks arbitrary-origin img beacons).
//   - Links restricted to http/https/mailto, forced target=_blank + rel.
// rehype-sanitize does NOT add target/rel, so the <a> renderer enforces them.

const schema = {
  ...defaultSchema,
  tagNames: (defaultSchema.tagNames ?? []).filter((t) => t !== 'img'),
  protocols: {
    ...defaultSchema.protocols,
    href: ['http', 'https', 'mailto'],
  },
}

const components: Components = {
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="underline hover:no-underline"
    >
      {children}
    </a>
  ),
}

interface BannerMarkdownProps {
  children: string
  className?: string
}

/** Sanitized inline markdown renderer shared by banner strip, popup, and preview. */
export default function BannerMarkdown({ children, className }: BannerMarkdownProps) {
  return (
    <span className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeSanitize, schema]]}
        disallowedElements={['img']}
        unwrapDisallowed
        components={components}
      >
        {children}
      </ReactMarkdown>
    </span>
  )
}
